// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { spacing } from '@iheartjane/reefer-emotion';
import type { MouseEventHandler, ReactChild } from 'react';

import { useScrollToElement } from '../hooks/useScrollToElement';

const Link = styled.a({
  ...spacing({ px: 24, py: 8 }),
  background: 'white',
  zIndex: 'var(--layers-toast)',
  position: 'absolute',
  left: '-10000px',
  top: 'auto',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  '&:focus': {
    position: 'fixed',
    left: 0,
    width: 'auto',
    height: 'auto',
  },
});

interface SkipLinkProps {
  children: ReactChild;
  targetElementId: string;
}

const forceFocus = (element: HTMLElement) => {
  element.focus({ preventScroll: true });
  if (document.activeElement !== element) {
    element.setAttribute('tabindex', '-1');
    element.focus({ preventScroll: true });
  }
  return;
};

const SkipLink = ({ targetElementId, children }: SkipLinkProps) => {
  const scrollToElement = useScrollToElement(targetElementId);

  const handleClick: MouseEventHandler = (e) => {
    e.preventDefault();
    const shadowHost = document.getElementById('shadow-host')?.shadowRoot;
    let element = shadowHost?.getElementById(targetElementId);
    element = element || document.getElementById(targetElementId);

    scrollToElement();

    if (!element) return;
    forceFocus(element);
  };

  return (
    <Link onClick={handleClick} href={`#${targetElementId}`}>
      {children}
    </Link>
  );
};

export const SkipLinks = () => {
  const currentlyOnSearchResults =
    (window.location.pathname.includes('brands/') &&
      !window.location.pathname.includes('janegold') &&
      !window.location.pathname.includes('products/')) ||
    (window.location.pathname.includes('products') &&
      !window.location.pathname.includes('products/')) ||
    ((window.location.pathname.includes('menu') ||
      window.location.pathname.includes('stores')) &&
      !window.location.pathname.includes('/menu/products/'));

  const currentlyOnStoreSearchPage =
    window.location.pathname.includes('stores') &&
    !window.location.pathname.includes('stores/');

  if (!currentlyOnSearchResults && currentlyOnStoreSearchPage) return null;

  return (
    <>
      {currentlyOnSearchResults && (
        <>
          <SkipLink targetElementId="results-filter">
            Skip to Results Filters
          </SkipLink>
          <SkipLink targetElementId="results">Skip to Results</SkipLink>
        </>
      )}
      {currentlyOnStoreSearchPage ? null : (
        <SkipLink targetElementId="content">Skip to Main Content</SkipLink>
      )}
    </>
  );
};
